// ===========================================
// AUTH SLICE
// ===========================================

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { LoadingState, Nullable } from '@/types';
import type { User, LoginCredentials, RegisterData, UpdateProfileData, AuthResponse } from '../types/auth.types';
import { tokenService, storageService, STORAGE_KEYS } from '@/services/external/storage';
import axios from 'axios';

// Configure Axios
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add interceptor for auth token
api.interceptors.request.use((config) => {
  const token = tokenService.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface AuthState {
  // User data
  user: Nullable<User>;
  isAuthenticated: boolean;

  // Loading states
  status: LoadingState;
  loginStatus: LoadingState;
  registerStatus: LoadingState;
  logoutStatus: LoadingState;
  checkUserStatus: LoadingState;

  // Errors
  error: Nullable<string>;
  loginError: Nullable<string>;
  registerError: Nullable<string>;

  // Session
  sessionChecked: boolean;

  // Login Flow State
  userExists: boolean;
  userHasPassword: boolean;
  userAvatar: Nullable<string>;
}

// Load initial state from storage
const loadInitialUser = (): Nullable<User> => {
  return storageService.get<User>(STORAGE_KEYS.USER);
};

const initialState: AuthState = {
  user: loadInitialUser(),
  isAuthenticated: tokenService.hasValidToken(),
  status: 'idle',
  loginStatus: 'idle',
  registerStatus: 'idle',
  logoutStatus: 'idle',
  checkUserStatus: 'idle',
  error: null,
  loginError: null,
  registerError: null,
  sessionChecked: false,
  userExists: false,
  userHasPassword: false,
  userAvatar: null,
};

// Async Thunks

export const checkUserAsync = createAsyncThunk(
  'auth/checkUser',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/check', { email });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check user');
    }
  }
);

export const sendOtpAsync = createAsyncThunk(
  'auth/sendOtp',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/send-otp', { email });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send OTP');
    }
  }
);

export const loginWithOtpAsync = createAsyncThunk(
  'auth/loginWithOtp',
  async ({ email, otp }: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await api.post<AuthResponse>('/auth/login-otp', { email, otp });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Invalid OTP');
    }
  }
);

export const loginAsync = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      // If OTP is provided, use OTP login
      if (credentials.otp) {
        const response = await api.post<AuthResponse>('/auth/login-otp', credentials);
        return response.data;
      }

      // Otherwise password login
      const response = await api.post<AuthResponse>('/login', credentials);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const registerAsync = createAsyncThunk(
  'auth/register',
  async (data: RegisterData, { rejectWithValue }) => {
    try {
      const response = await api.post<AuthResponse>('/register', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const logoutAsync = createAsyncThunk(
  'auth/logout',
  async (_, { }) => {
    try {
      await api.post('/logout'); // Best effort
    } catch (error) {
      // Ignore logout errors
    } finally {
      tokenService.clearTokens();
      storageService.remove(STORAGE_KEYS.USER);
    }
  }
);

export const updateProfileAsync = createAsyncThunk(
  'auth/updateProfile',
  async (data: UpdateProfileData, { rejectWithValue }) => {
    try {
      const response = await api.put<{ message: string, user: User }>('/user/profile', data);
      return response.data.user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Profile update failed');
    }
  }
);

export const checkSessionAsync = createAsyncThunk(
  'auth/checkSession',
  async (_, { rejectWithValue }) => {
    try {
      if (!tokenService.hasValidToken()) {
        return null;
      }
      const response = await api.get('/user');
      return response.data;
    } catch (error) {
      tokenService.clearTokens();
      return rejectWithValue((error as Error).message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      storageService.set(STORAGE_KEYS.USER, action.payload);
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      tokenService.clearTokens();
      storageService.remove(STORAGE_KEYS.USER);
    },
    setTokens: (_, action: PayloadAction<{ accessToken: string }>) => {
      tokenService.setAccessToken(action.payload.accessToken);
    },
    clearErrors: (state) => {
      state.error = null;
      state.loginError = null;
      state.registerError = null;
    },
    resetAuthState: (state) => {
      state.status = 'idle';
      state.loginStatus = 'idle';
      state.registerStatus = 'idle';
      state.error = null;
      state.loginError = null;
      state.registerError = null;
    },
    setSessionChecked: (state, action: PayloadAction<boolean>) => {
      state.sessionChecked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check User
      .addCase(checkUserAsync.pending, (state) => {
        state.checkUserStatus = 'loading';
      })
      .addCase(checkUserAsync.fulfilled, (state, action) => {
        state.checkUserStatus = 'succeeded';
        state.userExists = action.payload.exists;
        state.userHasPassword = action.payload.has_password;
        state.userAvatar = action.payload.avatar;
      })
      .addCase(checkUserAsync.rejected, (state, action) => {
        state.checkUserStatus = 'failed';
        state.error = action.payload as string;
      })

      // Login (Password or OTP)
      .addCase(loginAsync.pending, (state) => {
        state.loginStatus = 'loading';
        state.loginError = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loginStatus = 'succeeded';
        state.user = action.payload.user;
        state.isAuthenticated = true;
        tokenService.setAccessToken(action.payload.access_token);
        storageService.set(STORAGE_KEYS.USER, action.payload.user);
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loginStatus = 'failed';
        state.loginError = action.payload as string;
      })

      // Login With OTP (Specific)
      .addCase(loginWithOtpAsync.pending, (state) => {
        state.loginStatus = 'loading';
        state.loginError = null;
      })
      .addCase(loginWithOtpAsync.fulfilled, (state, action) => {
        state.loginStatus = 'succeeded';
        state.user = action.payload.user;
        state.isAuthenticated = true;
        tokenService.setAccessToken(action.payload.access_token);
        storageService.set(STORAGE_KEYS.USER, action.payload.user);
      })
      .addCase(loginWithOtpAsync.rejected, (state, action) => {
        state.loginStatus = 'failed';
        state.loginError = action.payload as string;
      })

      // Logout
      .addCase(logoutAsync.fulfilled, (state) => {
        state.logoutStatus = 'succeeded';
        state.user = null;
        state.isAuthenticated = false;
      })

      // Profile Update
      .addCase(updateProfileAsync.fulfilled, (state, action) => {
        state.user = action.payload;
        storageService.set(STORAGE_KEYS.USER, action.payload);
      })

      // Check Session
      .addCase(checkSessionAsync.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.sessionChecked = true;
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;
        }
      })
      .addCase(checkSessionAsync.rejected, (state) => {
        state.status = 'failed';
        state.sessionChecked = true;
        state.user = null;
        state.isAuthenticated = false;
      });
  },
});

export const {
  setUser,
  clearUser,
  setTokens,
  clearErrors,
  resetAuthState,
  setSessionChecked,
} = authSlice.actions;

export const authReducer = authSlice.reducer;

// Selectors
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthStatus = (state: { auth: AuthState }) => state.auth.status;
export const selectLoginStatus = (state: { auth: AuthState }) => state.auth.loginStatus;
export const selectCheckUserStatus = (state: { auth: AuthState }) => state.auth.checkUserStatus;
import { createSelector } from '@reduxjs/toolkit';

// ... (selectors)
export const selectUserFlow = createSelector(
  [
    (state: { auth: AuthState }) => state.auth.userExists,
    (state: { auth: AuthState }) => state.auth.userHasPassword,
    (state: { auth: AuthState }) => state.auth.userAvatar,
  ],
  (exists, hasPassword, avatar) => ({
    exists,
    hasPassword,
    avatar,
  })
);
export const selectLoginError = (state: { auth: AuthState }) => state.auth.loginError;
export const selectSessionChecked = (state: { auth: AuthState }) => state.auth.sessionChecked;
